import { useEffect, useMemo, useRef } from 'react';
import { Button, Card } from 'react-bootstrap';
import classNames from 'classnames';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import numberHumanizer from '@/helpers/numberHumanizer';
import formatCompactNumber from '@/helpers/formatCompactNumber';

type OpportunityQuery = {
  data?: unknown;
  isLoading: boolean;
  isFetching: boolean;
  isUninitialized: boolean;
  isError: boolean;
  refetch: () => unknown;
};

type OpportunityRow = {
  keyword: string;
  volume: number;
};

type GrowthTab = {
  key: string;
  label: string;
  tooltip?: string;
  query: OpportunityQuery;
};

type GrowthOpportunitiesCardProps = {
  active: string;
  onChange: (key: string) => void;
  tabs: GrowthTab[];
};

const decodeKeyword = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const extractRows = (raw: unknown): unknown[] => {
  if (Array.isArray(raw)) return raw;
  if (!raw || typeof raw !== 'object') return [];
  const data = (raw as { data?: unknown }).data;
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];
  const nested = data as { data?: unknown; rows?: unknown };
  if (Array.isArray(nested.data)) return nested.data;
  if (Array.isArray(nested.rows)) return nested.rows;
  return [];
};

const toOpportunityRows = (raw: unknown): OpportunityRow[] =>
  extractRows(raw)
    .map(item => {
      if (!item || typeof item !== 'object') return null;
      const row = item as { keyword?: unknown; keyword_search_volume?: unknown };
      if (typeof row.keyword !== 'string' || !row.keyword) return null;
      const volume = Number(row.keyword_search_volume);
      return {
        keyword: decodeKeyword(row.keyword),
        volume: Number.isFinite(volume) ? volume : 0
      };
    })
    .filter((row): row is OpportunityRow => row !== null)
    .slice(0, 7);

const OpportunityPreload = () => (
  <div className="placeholder-glow d-flex flex-column" aria-hidden>
    {Array.from({ length: 7 }, (_, index) => (
      <div key={index} className="d-flex align-items-center gap-2 px-1 py-2">
        <span
          className="placeholder rounded-circle flex-shrink-0"
          style={{ width: 22, height: 22 }}
        />
        <span
          className="placeholder rounded"
          style={{ height: 12, width: `${78 - (index % 3) * 14}%` }}
        />
        <span
          className="placeholder rounded flex-shrink-0 ms-auto"
          style={{ width: 36, height: 12 }}
        />
      </div>
    ))}
  </div>
);

const OpportunityList = ({
  rows,
  barClass
}: {
  rows: OpportunityRow[];
  barClass: string;
}) => {
  const maxVolume = Math.max(...rows.map(row => row.volume), 0);
  return (
    <div className="d-flex flex-column">
      {rows.map((row, index) => {
        const width =
          maxVolume > 0 ? Math.max(4, Math.round((row.volume / maxVolume) * 100)) : 0;
        return (
          <div
            key={`${row.keyword}-${index}`}
            className={classNames('d-flex align-items-center gap-2 py-2', {
              'border-bottom border-200': index < rows.length - 1
            })}
          >
            <span
              className="text-500 fs--2 fw-semi-bold text-center flex-shrink-0"
              style={{ width: 18 }}
            >
              {index + 1}
            </span>
            <div className="flex-grow-1 overflow-hidden">
              <div className="text-900 fs--1 text-truncate">{row.keyword}</div>
              <div className="mt-1 rounded-pill bg-300" style={{ height: 4 }}>
                <div
                  className={classNames('rounded-pill', barClass)}
                  style={{ width: `${width}%`, height: 4 }}
                />
              </div>
            </div>
            <span
              className="text-700 fs--2 fw-semi-bold text-nowrap flex-shrink-0"
              title={numberHumanizer(row.volume)}
            >
              {formatCompactNumber(row.volume)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const GrowthOpportunitiesCard = ({
  active,
  onChange,
  tabs
}: GrowthOpportunitiesCardProps) => {
  const current = tabs.find(tab => tab.key === active) ?? tabs[0];
  const parsedRows = useMemo(
    () => toOpportunityRows(current?.query.data),
    [current?.query.data]
  );
  const rowsByTab = useRef<Record<string, OpportunityRow[]>>({});
  useEffect(() => {
    if (!current || parsedRows.length === 0) return;
    rowsByTab.current[current.key] = parsedRows;
  }, [current, parsedRows]);
  const rows =
    parsedRows.length > 0
      ? parsedRows
      : (current && rowsByTab.current[current.key]) || [];
  const waiting = rows.length === 0 && !!current && !current.query.isError && (
    current.query.isUninitialized ||
    current.query.isLoading ||
    current.query.isFetching
  );

  return (
    <Card className="h-100 w-100">
      <FalconCardHeader
        title={
          <div>
            فرصت های رشد
            <HelpTooltip
              html={
                'تب‌های فراموش‌شده و ضعیف اهمیت بالایی دارن. در این بخش، پرجستجوترین کلمات این دو تب نمایش داده میشه. اگر این کلمات با حوزه سایت شما مرتبط هست، توصیه میشه برای آن‌ها برنامه‌ریزی کن.'
              }
            />
          </div>
        }
        titleTag="h6"
        className="py-2"
        light
      />
      <Card.Body className="d-flex flex-column" style={{ minHeight: 360 }}>
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          {tabs.map(tab => {
            const selected = tab.key === current?.key;
            return (
              <div key={tab.key} className="d-inline-flex align-items-center">
                <Button
                  type="button"
                  size="sm"
                  variant={selected ? 'falcon-primary' : 'falcon-default'}
                  className={classNames('rounded-pill', {
                    'fw-semi-bold': selected
                  })}
                  onClick={() => onChange(tab.key)}
                >
                  {tab.label}
                </Button>
                {tab.tooltip ? (
                  <HelpTooltip html={tab.tooltip} placement="top" />
                ) : null}
              </div>
            );
          })}
        </div>
        {rows.length > 0 ? (
          <OpportunityList
            rows={rows}
            barClass={current?.key === 'weak' ? 'bg-warning' : 'bg-primary'}
          />
        ) : waiting ? (
          <OpportunityPreload />
        ) : current?.query.isError ? (
          <div className="d-flex flex-column align-items-center justify-content-center gap-2 flex-grow-1 text-center">
            <p className="text-700 fs--1 mb-0">فرصت‌های رشد بارگذاری نشد.</p>
            <Button
              size="sm"
              variant="falcon-default"
              onClick={() => current.query.refetch()}
            >
              تلاش دوباره
            </Button>
          </div>
        ) : (
          <p className="text-700 fs--1 mb-0 text-center py-5">
            کلمه‌ای در این بخش نیست.
          </p>
        )}
      </Card.Body>
    </Card>
  );
};

export default GrowthOpportunitiesCard;
